import React from "react";
import { formatMessage, formatTicketUrl } from "../utils/formattingUtils";
import type { ZendeskConversationMessage, ZendeskRequester, ZendeskTicket, ZendeskTicketMessage } from "../types";
import { zendesk } from "../zendesk";

type Props = {
  children(props: { ticket: ZendeskTicket }): React.JSX.Element;
};

/**
 * Loads the current Zendesk ticket context and normalizes it before child views render Linear-specific flows.
 *
 * Use this wrapper around authenticated app views such as {@link Main} so descendants receive a consistent
 * {@link ZendeskTicket} shape.
 */
export function LoadZendeskData(props: Props) {
  const [ticket, setTicket] = React.useState<ZendeskTicket | undefined>();

  React.useEffect(() => {
    void fetchData();
  }, []);

  const fetchData = async () => {
    const [zdData, contextData] = await Promise.all([
      zendesk.get([
        "ticket.id",
        "ticket.type",
        "ticket.priority",
        "ticket.subject",
        "ticket.description",
        "ticket.status",
        "ticket.createdAt",
        "ticket.brand.url",
        "ticket.requester",
        "ticket.conversation",
      ]),
      zendesk.context(),
    ]);
    if (zdData && contextData) {
      const requester = zdData["ticket.requester"] as ZendeskRequester;
      const conversationMessages = zdData["ticket.conversation"] as ZendeskConversationMessage[];
      const messages: ZendeskTicketMessage[] = conversationMessages.map(msg => {
        const authorName = msg.author.name;
        return {
          subject: authorName === requester.name && requester.email ? `${authorName} (${requester.email})` : authorName,
          body: formatMessage(msg),
          timestamp: msg.timestamp,
        };
      });

      setTicket({
        id: zdData["ticket.id"],
        type: zdData["ticket.type"],
        priority: zdData["ticket.priority"],
        domain: zdData["ticket.brand.url"],
        url: formatTicketUrl(contextData.account.subdomain, zdData["ticket.id"]),
        subject: zdData["ticket.subject"],
        status: zdData["ticket.status"],
        createdAt: new Date(zdData["ticket.createdAt"]),
        firstMessage: messages[0],
        allMessages: messages,
      });
    }
  };

  return ticket ? props.children({ ticket }) : <div />;
}
